import { useMemo, useState } from "react";
import useFormatter from "@coworker/app/src/hooks/useFormatter";
import { useTranslation } from "@coworker/locales";
import { getByPath } from "@coworker/apprestructured/src/shared/helpers/object.helper";
import { FixaText } from "@coworker/apprestructured/src/shared/wrappers/FixaText/FixaText";
import { FixaSegmentedControl } from "@coworker/apprestructured/src/shared/wrappers/FixaSegmentedControl/FixaSegmentedControl";
import { ViewerFragmentCommonProps } from "@coworker/apprestructured/src/task-management/components/TaskViewerSheet/viewer-fragments/ViewerFragment";
import styles from "./TabTitleContentViewerFragment.module.css";
import {
  shouldShowField,
  VisibilityCondition,
} from "@coworker/apprestructured/src/task-management/helpers/visibilityCondition.helper";
import { isLoading } from "@coworker/apprestructured/src/task-management/loading";

export type TabTitleContentViewerFragmentTabConfig = {
  title: string;
  description: string;
  amount: string;
  currency: string;
};

export type TabTitleContentViewerFragmentConfigProps = {
  tabs: TabTitleContentViewerFragmentTabConfig[];
  visibility?: VisibilityCondition;
};

type TabTitleContentViewerFragmentProps = {
  configProps: TabTitleContentViewerFragmentConfigProps;
  commonProps: ViewerFragmentCommonProps;
};

function TabTitleContentViewerFragment(
  props: TabTitleContentViewerFragmentProps
) {
  const { tabs, visibility } = props.configProps;
  const { task } = props.commonProps;
  const { t } = useTranslation();
  const { formatCurrency } = useFormatter();
  const [selectedTab, setSelectedTab] = useState(0);

  const mappedTabs = useMemo(() => {
    return tabs
      .map(({ title, description, amount, currency }, index) => {
        const amountValue = getByPath(task, amount);
        const currencyValue = getByPath(task, currency);

        return {
          id: `tab-title-content-${index}`,
          title: t(title),
          description: description,
          amount:
            amountValue &&
            currencyValue &&
            formatCurrency(amountValue, currencyValue),
        };
      })
      .filter(
        ({ amount, description }) => description || (!description && amount)
      );
  }, [task, tabs, t, formatCurrency]);

  if (visibility && !isLoading(task) && !shouldShowField(task, visibility)) {
    return null;
  }

  if (!mappedTabs.length) return null;

  const activeTab = mappedTabs[selectedTab];

  return (
    <div className={`${styles["card"]} tab-title-content-viewer-fragment-card`}>
      <FixaSegmentedControl
        items={mappedTabs.map((tab) => ({
          label: tab.title,
        }))}
        activeIndex={Math.min(selectedTab, mappedTabs.length - 1)}
        onClick={(_, index) => setSelectedTab(index)}
        size="small"
      />
      {activeTab && (
        <div className={styles["content"]}>
          <FixaText className={styles["description"]}>
            {t(activeTab.description)}
          </FixaText>
          <FixaText className={styles["amount"]}>{activeTab.amount}</FixaText>
        </div>
      )}
    </div>
  );
}

export default TabTitleContentViewerFragment;
